import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Mail, TrendingUp } from "lucide-react";

const datasets = [
  {
    revenue: "$421,800",
    growth: "24% growth this month",
    path: "M0 50 Q40 35 80 40 T160 25 T200 30",
  },
  {
    revenue: "$389,200",
    growth: "18% growth this month",
    path: "M0 55 Q40 45 80 35 T160 30 T200 25",
  },
  {
    revenue: "$452,600",
    growth: "31% growth this month",
    path: "M0 60 Q40 40 80 30 T160 20 T200 28",
  },
];

const feed = [
  "Report sent → Aspen Chalet",
  "Pricing updated → Manhattan Loft",
  "Ranking improved → Malibu Beach House",
  "Revenue spike → Dubai Marina Suite",
  "High demand → Santorini Villa",
];

export default function FeatureVisualReporting() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const i = setInterval(() => {
      setIndex((p) => (p + 1) % datasets.length);
    }, 3000);
    return () => clearInterval(i);
  }, []);

  const current = datasets[index];

  return (
    <div className="
      w-full h-full
      rounded-[28px]
      p-4 sm:p-5
      bg-gradient-to-br from-brand-primary via-[#0f2f2a] to-[#081a18]
      flex flex-col justify-between
      relative overflow-hidden
    ">

      {/* GLOW */}
      <div className="absolute -top-20 -right-20 w-[200px] h-[200px] bg-bg-light/10 blur-3xl rounded-full" />

      {/* LIVE */}
      <div className="absolute top-4 right-4 flex items-center gap-2 text-bg-surface text-micro">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute h-full w-full rounded-full bg-green-400 opacity-70" />
          <span className="relative h-2 w-2 rounded-full bg-green-400" />
        </span>
        Live
      </div>

      {/* 🔝 HEADER + KPI */}
      <div className="flex flex-col gap-2 relative z-10">

        <p className="text-small font-medium text-bg-light">
          Portfolio revenue
        </p>

        {/* 🔥 CHANGE ANIMATION (BEST PRACTICE) */}
        <motion.p
          key={current.revenue}
          initial={{ opacity: 0.6, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="text-[34px] sm:text-[42px] font-bold text-bg-surface leading-tight"
        >
          {current.revenue}
        </motion.p>

        <div className="flex items-center gap-2 text-small text-bg-light">
          <TrendingUp size={14} className="text-bg-surface" />
          {current.growth}
        </div>

      </div>

      {/* 📈 GRAPH */}
      <div className="relative z-10">

        {/* GRID */}
        <div className="absolute inset-0 opacity-10">
          <div className="grid grid-rows-4 h-full">
            <div className="border-t border-bg-surface/20" />
            <div className="border-t border-bg-surface/20" />
            <div className="border-t border-bg-surface/20" />
            <div className="border-t border-bg-surface/20" />
          </div>
        </div>

        <svg viewBox="0 0 200 70" className="w-full h-[70px] sm:h-[80px]">

          {/* LINE */}
          <motion.path
            key={current.path}
            d={current.path}
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.2 }}
          />

          {/* AREA */}
          <motion.path
            d={`${current.path} L200 70 L0 70 Z`}
            fill="url(#fill)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          />

          {/* 🔥 ACTIVE DOT (BIG UPGRADE) */}
          <motion.circle
            cx="200"
            cy="30"
            r="3"
            fill="#ffffff"
            animate={{ scale: [1, 1.4, 1] }}
            transition={{ repeat: Infinity, duration: 1.6 }}
          />

          <defs>
            <linearGradient id="fill">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
          </defs>

        </svg>
      </div>

      {/* 📤 FEED */}
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="
          flex items-center justify-between
          border border-bg-surface/20
          rounded-xl px-3 py-2
          bg-bg-surface/10 backdrop-blur
          relative z-10
        "
      >
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-bg-surface" />
          <span className="text-small text-bg-surface">
            {feed[index]}
          </span>
        </div>

        <span className="text-micro text-bg-light">
          now
        </span>
      </motion.div>

      {/* 🔽 METRICS */}
      <div className="
        grid grid-cols-3 gap-3
        pt-3 border-t border-bg-surface/20
        text-bg-surface text-center relative z-10
      ">

        <div className="flex flex-col items-center">
          <p className="text-micro text-bg-light">Bookings</p>
          <p className="text-small font-semibold mt-1">27</p>
        </div>

        <div className="flex flex-col items-center">
          <p className="text-micro text-bg-light">Occupancy</p>
          <p className="text-small font-semibold mt-1">83%</p>
        </div>

        <div className="flex flex-col items-center">
          <p className="text-micro text-bg-light">Profit</p>
          <p className="text-small font-semibold mt-1">+12%</p>
        </div>

      </div>

    </div>
  );
}